import {
  Injectable,
  signal,
  computed,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { IOperatingSystemInfo } from '../interfaces/IOperatingSystemInfo.interface';
import { IUserAgentDataValues } from '../interfaces/IUserAgentDataValues.interface';
import { BROWSERSLIST_MIN_VERSIONS } from '../config/browserslist-min-versions.config';

@Injectable({
  providedIn: 'root',
})
export class UserAgentService {
  private readonly STORAGE_KEY = 'dismissed_outdated_os_warning';
  private readonly MIN_SUPPORTED_VERSIONS = BROWSERSLIST_MIN_VERSIONS;

  private readonly isDismissed = signal<boolean>(
    typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(this.STORAGE_KEY) === 'true',
  );

  public readonly osInfo = signal<IOperatingSystemInfo>({
    name: 'Unknown',
    version: '',
    isOutdated: false,
  });

  public readonly shouldShowBanner = computed(
    () => this.osInfo().isOutdated && !this.isDismissed(),
  );

  public init(): Promise<void> {
    return this.detectOS();
  }

  public dismissWarning(): void {
    this.isDismissed.set(true);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(this.STORAGE_KEY, 'true');
    }
  }

  public async detectOS(): Promise<void> {
    const nav = navigator as unknown as {
      userAgentData?: {
        getHighEntropyValues: (
          hints: string[],
        ) => Promise<IUserAgentDataValues>;
      };
    };

    if (nav.userAgentData?.getHighEntropyValues) {
      try {
        const data = await nav.userAgentData.getHighEntropyValues([
          'platform',
          'platformVersion',
          'fullVersionList',
        ]);

        if (data.platform && data.platform.trim() !== '') {
          const chromeEntry = data.fullVersionList?.find(
            (b: { brand: string; version: string }) =>
              b.brand === 'Chromium' || b.brand === 'Google Chrome',
          );

          let chromeVersion = 0;
          if (chromeEntry?.version) {
            chromeVersion =
              Number.parseInt(chromeEntry?.version.split('.')[0], 10) || 0;
          }

          this.evaluateVersion(
            data.platform,
            data.platformVersion || '',
            chromeVersion,
          );
          return;
        }
      } catch {
        // Fallback to User-Agent parsing if Client Hints API fails
      }
    }
    this.parseUserAgentFallback();
  }

  private parseUserAgentFallback(): void {
    const ua = navigator.userAgent;

    // macOS check (browserslist: last 2 Safari versions)
    if (ua.includes('Mac OS X')) {
      const match = new RegExp(/Mac OS X (\d+[._]\d+)/).exec(ua);
      if (match) {
        const versionStr = match[1].replace('_', '.');
        const versionNum = Number.parseFloat(versionStr);
        const isOutdated = versionNum < this.MIN_SUPPORTED_VERSIONS.macOSLegacy;
        this.setOsState('macOS', versionStr, isOutdated);
        return;
      }
    }

    // iOS check (browserslist: iOS >= 15)
    if (/iPhone|iPad|iPod/.test(ua)) {
      const match = new RegExp(/OS (\d+)[_.]/).exec(ua);
      if (match) {
        const major = Number.parseInt(match[1], 10);
        this.setOsState(
          'iOS',
          `${major}`,
          major < this.MIN_SUPPORTED_VERSIONS.ios,
        );
        return;
      }
    }

    // Android check (browserslist: last 2 Android major versions)
    if (ua.includes('Android')) {
      const match = new RegExp(/Android (\d+(\.\d+)?)/).exec(ua);
      if (match) {
        const major = Number.parseInt(match[1], 10);
        this.setOsState(
          'Android',
          match[1],
          major < this.MIN_SUPPORTED_VERSIONS.android,
        );
        return;
      }
    }

    // Chrome OS check (browserslist: last 2 Chrome versions)
    if (ua.includes('CrOS')) {
      const match = new RegExp(/CrOS \w+ (\d+)/).exec(ua);
      if (match) {
        const build = Number.parseInt(match[1], 10);
        this.setOsState(
          'ChromeOS',
          match[1],
          build < this.MIN_SUPPORTED_VERSIONS.chromeOsBuild,
        );
        return;
      }
    }

    this.setOsState('Other', '', false);
  }

  private evaluateVersion(
    platform: string,
    platformVersion: string,
    androidAddInfo: number,
  ): void {
    const majorVersion =
      Number.parseInt(platformVersion.split('.')[0], 10) || 0;
    let outdated = false;

    switch (platform.toLowerCase()) {
      case 'macos':
        outdated = majorVersion < this.MIN_SUPPORTED_VERSIONS.macOS;
        break;

      case 'android':
        outdated = androidAddInfo < this.MIN_SUPPORTED_VERSIONS.android;
        break;

      case 'ios':
        outdated = majorVersion < this.MIN_SUPPORTED_VERSIONS.ios;
        break;

      case 'chrome os':
      case 'chromeos':
      case 'cros':
        outdated = majorVersion < this.MIN_SUPPORTED_VERSIONS.chromeOsBuild;
        break;
    }

    this.setOsState(platform, platformVersion, outdated);
  }

  private setOsState(name: string, version: string, isOutdated: boolean): void {
    this.osInfo.set({ name, version, isOutdated });
  }
}

export function provideDeviceDetectorInitializer() {
  return provideAppInitializer(() => {
    const service = inject(UserAgentService);
    return service.init();
  });
}
